export const PRINT_PREF_KEY1 = 'print'

export type PrintFormat = {
  value: string,
  title: string,
  widthMm: number,
  heightMm: number,
  /** Größen-Token für den bestehenden PDF-Dienst. */
  sizeToken: string,
}

export const PrintFormats: PrintFormat[] = [
  { value: 'flip', title: 'Flip', widthMm: 655, heightMm: 995, sizeToken: '_670x990' },
  { value: 'pin', title: 'Pin', widthMm: 1040, heightMm: 1350, sizeToken: '_1040x1350' },
  { value: 'posterA2', title: 'Plakat DIN A2', widthMm: 420, heightMm: 594, sizeToken: '_594x420' },
  { value: 'handoutA3', title: 'Handout DIN A3', widthMm: 297, heightMm: 420, sizeToken: '_297x420' },
  { value: 'handoutA4', title: 'Handout DIN A4', widthMm: 210, heightMm: 297, sizeToken: '_210x297' },
  { value: 'handoutA5', title: 'Handout DIN A5', widthMm: 148, heightMm: 210, sizeToken: '_148x210' },
  { value: 'moderationA6', title: 'Moderationskarte DIN A6', widthMm: 105, heightMm: 148, sizeToken: '_105x148' },
  { value: 'cardA7', title: 'Lernkarte DIN A7', widthMm: 74, heightMm: 105, sizeToken: '_74x105' },
  { value: 'moderationLong', title: 'Moderationskarte lang', widthMm: 95, heightMm: 205, sizeToken: '_95x205' },
  { value: 'envelopeDl', title: 'Umschlag DIN lang', widthMm: 220, heightMm: 110, sizeToken: '_220x110' },
  { value: 'envelopeC5', title: 'Umschlag DIN C5', widthMm: 229, heightMm: 162, sizeToken: '_229x162' },
  { value: 'envelopeC4', title: 'Umschlag DIN C4', widthMm: 229, heightMm: 324, sizeToken: '_229x324' },
]

export const PrintOrientations = [
  { value: '', title: 'hoch' },
  { value: 'landscape', title: 'quer' },
]

export const PrintSides = [
  { value: '', title: 'einseitig' },
  { value: 'duplex', title: 'doppelseitig' },
]

export const PrintColors = [
  { value: '', title: 'Bunt' },
  { value: 'gray', title: 'Grau' },
]

export const PrintProcessings = [
  { value: '', title: 'lose' },
  { value: 'stapled', title: 'getackert' },
  { value: 'bound', title: 'geheftet' },
]

export const PrintCountModes = [
  { value: '', title: 'aus System' },
  { value: 'fixed', title: 'fest pro Training' },
]

const processingRank: Record<string, number> = {
  '': 0,
  loose: 0,
  stapled: 1,
  bound: 2,
}

export function printerPreferenceKey(formatValue: string) {
  return `printer.${formatValue}`
}

export function mediaPreferenceKey(formatValue: string) {
  return `media.${formatValue}`
}

export function findPrintFormat(value: string): PrintFormat | undefined {
  return PrintFormats.find(format => format.value === value)
}

export type PrintSettingGroup<T> = {
  key: string,
  headline: string,
  sizeToken: string,
  formatValue: string,
  formatTitle: string,
  widthMm: number,
  heightMm: number,
  orientation: 'portrait' | 'landscape',
  sides: 'simplex' | 'duplex',
  color: 'color' | 'gray',
  items: T[],
}

type PrintItem = {
  getPropV1: (key1: string, key2: string) => string,
  count: number,
}

function optionTitle(options: {value: string, title: string}[], value: string) {
  return options.find(option => option.value === value)?.title || value
}

export function standardPrintCount(item: PrintItem, participantCount: number): number {
  if (item.getPropV1('data', 'printCountMode') === 'fixed') {
    const raw = item.getPropV1('data', 'printCountFixed')
    const fixed = parseInt(raw, 10)
    if (raw !== '' && !Number.isNaN(fixed)) {
      return fixed
    }
  }
  const userFactor = parseInt(item.getPropV1('data', 'copiesPerPerson') || '0', 10)
  if (userFactor > 0) {
    return (userFactor * (participantCount || 0) + 2)
  }
  return item.count
}

export function groupByPrintSettings<T extends PrintItem>(items: T[]): PrintSettingGroup<T>[] {
  const groups = new Map<string, PrintSettingGroup<T> & { sortFormat: number, sortProcessing: number }>()
  items.forEach(item => {
    const format = findPrintFormat(item.getPropV1('data', 'printFormat'))
    if (!format) { return }
    const orientationValue = item.getPropV1('data', 'printOrientation') === 'landscape' ? 'landscape' : ''
    const sidesValue = item.getPropV1('data', 'printSides') === 'duplex' ? 'duplex' : ''
    const colorValue = item.getPropV1('data', 'printColor') === 'gray' ? 'gray' : ''
    const processingRaw = item.getPropV1('data', 'printProcessing')
    const processingValue = processingRaw === 'stapled' || processingRaw === 'bound' ? processingRaw : ''
    const individual = item.getPropV1('data', 'printIndividualGroup').trim()
    const key = [
      individual || format.value,
      format.value,
      orientationValue,
      sidesValue,
      colorValue,
      processingValue,
    ].join('|')
    const sortFormat = PrintFormats.findIndex(entry => entry.value === format.value)
    const sortProcessing = processingRank[processingValue] ?? 0
    const existing = groups.get(key)
    if (existing) {
      existing.items.push(item)
      return
    }
    const name = individual ? `${individual} (${format.title})` : format.title
    groups.set(key, {
      key,
      headline: `${name} · ${format.widthMm} × ${format.heightMm} mm · ${optionTitle(PrintOrientations, orientationValue)} · ${optionTitle(PrintSides, sidesValue)} · ${optionTitle(PrintColors, colorValue)} · ${optionTitle(PrintProcessings, processingValue)}`,
      sizeToken: format.sizeToken,
      formatValue: format.value,
      formatTitle: format.title,
      widthMm: format.widthMm,
      heightMm: format.heightMm,
      orientation: orientationValue === 'landscape' ? 'landscape' : 'portrait',
      sides: sidesValue === 'duplex' ? 'duplex' : 'simplex',
      color: colorValue === 'gray' ? 'gray' : 'color',
      items: [item],
      sortFormat,
      sortProcessing,
    })
  })
  return Array.from(groups.values()).sort((a, b) => {
    if (a.sortFormat !== b.sortFormat) { return a.sortFormat - b.sortFormat }
    return a.sortProcessing - b.sortProcessing
  })
}
